import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DataTable from '../components/ResponsiveDataTable';
import { toast } from 'react-toastify';
import { apiCall, API_BASE_URL, getWarehouses, getProducts } from '../config/api';
import useIsMobile from '../hooks/useIsMobile';

const B2BProducts = () => {
  const isMobile = useIsMobile();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [showEditProductModal, setShowEditProductModal] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [editProduct, setEditProduct] = useState(null);
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [selectedModalWarehouses, setSelectedModalWarehouses] = useState([]);
  const [deletingProductId, setDeletingProductId] = useState(null);
  const [productForm, setProductForm] = useState({
    product_id: '',
    warehouse_id: '',
    min_order_qty: '',
    increment_count: '',
    b2b_price_per_unit: '',
    advance_pct: '20',
    free_delivery_allowed: false,
    delivery_fee_per_moq: '0',
    price_validity_start: '',
    price_validity_end: ''
  });

  useEffect(() => {
    loadWarehouses();
    loadCatalogProducts();
  }, []);

  useEffect(() => {
    if (selectedWarehouse) {
      loadProducts();
    }
  }, [selectedWarehouse]);

  const loadWarehouses = async () => {
    try {
      const res = await getWarehouses();
      if (res.success) setWarehouses(res.data || []);
    } catch (err) { console.error(err); }
  };

  const loadCatalogProducts = async () => {
    try {
      const res = await getProducts();
      if (res.success && res.data) {
        setCatalogProducts(res.data || []);
      }
    } catch (err) { console.error('Failed to load catalog products:', err); }
  };

  const loadProducts = async () => {
    setLoading(true);
    try {
      const res = await apiCall(`${API_BASE_URL}/b2b/oms/products?warehouseId=${selectedWarehouse}`);
      if (res.success) setProducts(res.data || []);
    } catch (err) { toast.error('Failed to load B2B products'); }
    finally { setLoading(false); }
  };

  const handleAddProduct = async () => {
    if (!productForm.product_id || selectedModalWarehouses.length === 0 || !productForm.min_order_qty || !productForm.b2b_price_per_unit || !productForm.increment_count || !productForm.price_validity_start || !productForm.price_validity_end) {
      toast.error('Fill all required fields and select at least one warehouse');
      return;
    }
    try {
      const res = await apiCall(`${API_BASE_URL}/b2b/oms/products/add`, {
        method: 'POST', body: JSON.stringify({
          ...productForm,
          product_id: parseInt(productForm.product_id),
          warehouse_ids: selectedModalWarehouses.map(Number),
          min_order_qty: parseInt(productForm.min_order_qty),
          increment_count: parseInt(productForm.increment_count),
          b2b_price_per_unit: parseFloat(productForm.b2b_price_per_unit),
          advance_pct: parseInt(productForm.advance_pct),
          delivery_fee_per_moq: parseFloat(productForm.delivery_fee_per_moq)
        })
      });
      if (res.success) {
        toast.success('B2B product added');
        setShowAddProductModal(false);
        resetProductForm();
        loadProducts();
      } else toast.error(res.message || 'Failed');
    } catch (err) { toast.error(err.message || 'Failed to add product'); }
  };

  const handleUpdateProduct = async () => {
    try {
      const res = await apiCall(`${API_BASE_URL}/b2b/oms/products/update`, {
        method: 'PUT', body: JSON.stringify({
          id: editProduct.id,
          min_order_qty: parseInt(productForm.min_order_qty),
          increment_count: parseInt(productForm.increment_count),
          b2b_price_per_unit: parseFloat(productForm.b2b_price_per_unit),
          advance_pct: parseInt(productForm.advance_pct),
          free_delivery_allowed: productForm.free_delivery_allowed,
          delivery_fee_per_moq: parseFloat(productForm.delivery_fee_per_moq),
          price_validity_start: productForm.price_validity_start,
          price_validity_end: productForm.price_validity_end,
          warehouseIds: selectedModalWarehouses.map(Number)
        })
      });
      if (res.success) {
        toast.success('Product updated');
        setShowEditProductModal(false);
        loadProducts();
      } else toast.error(res.message || 'Failed');
    } catch (err) { toast.error(err.message || 'Failed to update'); }
  };

  const handleToggleProduct = async (id) => {
    try {
      const res = await apiCall(`${API_BASE_URL}/b2b/oms/products/toggle`, {
        method: 'POST', body: JSON.stringify({ id })
      });
      if (res.success) { toast.success('Toggled'); loadProducts(); }
    } catch (err) { toast.error('Failed to toggle'); }
  };

  const handleDeleteProduct = async (product) => {
    const sku = product.product?.skuCode || 'this product';
    if (!window.confirm(`Delete ${sku} from this hub's B2B catalog?`)) return;

    setDeletingProductId(product.id);
    try {
      const res = await apiCall(`${API_BASE_URL}/b2b/oms/products/${product.id}`, {
        method: 'DELETE'
      });
      if (res.success) {
        setProducts(current => current.filter(item => item.id !== product.id));
        toast.success('B2B product deleted');
      } else {
        toast.error(res.message || 'Failed to delete product');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to delete product');
    } finally {
      setDeletingProductId(null);
    }
  };

  const openEditModal = (product) => {
    setEditProduct(product);
    setProductForm({
      product_id: product.product_id,
      warehouse_id: product.warehouse_id,
      min_order_qty: String(product.min_order_qty),
      increment_count: String(product.increment_count),
      b2b_price_per_unit: String(product.b2b_price_per_unit),
      advance_pct: String(product.advance_pct),
      free_delivery_allowed: product.free_delivery_allowed,
      delivery_fee_per_moq: String(product.delivery_fee_per_moq),
      price_validity_start: product.price_validity_start || '',
      price_validity_end: product.price_validity_end || ''
    });
    setSelectedModalWarehouses([String(product.warehouse_id)]);
    setShowEditProductModal(true);
  };

  const resetProductForm = () => {
    setProductForm({
      product_id: '', warehouse_id: selectedWarehouse || '', min_order_qty: '', increment_count: '',
      b2b_price_per_unit: '', advance_pct: '20', free_delivery_allowed: false, delivery_fee_per_moq: '0',
      price_validity_start: '', price_validity_end: ''
    });
    setSelectedModalWarehouses(selectedWarehouse ? [selectedWarehouse] : []);
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN') : 'N/A';

  const columns = [
    { name: 'SKU', selector: row => row.product?.skuCode || 'N/A', sortable: true, width: '150px' },
    { name: 'Product', selector: row => row.product?.name || 'N/A', sortable: true, width: '200px' },
    { name: 'MOQ', selector: row => row.min_order_qty, sortable: true, width: '80px' },
    { name: 'Increment', selector: row => row.increment_count, width: '90px' },
    { name: 'B2B Price', selector: row => row.b2b_price_per_unit, sortable: true, format: row => `₹${row.b2b_price_per_unit}`, width: '100px' },
    { name: 'Advance %', selector: row => row.advance_pct, format: row => `${row.advance_pct}%`, width: '90px' },
    { name: 'Free Del.', selector: row => row.free_delivery_allowed ? 'Yes' : 'No', width: '80px' },
    { name: 'Fee/MOQ', selector: row => row.delivery_fee_per_moq, format: row => `₹${row.delivery_fee_per_moq}`, width: '80px' },
    { name: 'Valid From', selector: row => row.price_validity_start, format: row => formatDate(row.price_validity_start), width: '100px' },
    { name: 'Valid To', selector: row => row.price_validity_end, format: row => formatDate(row.price_validity_end), width: '100px' },
    { name: 'Active', cell: row => (
      <input type="checkbox" checked={row.is_active} onChange={() => handleToggleProduct(row.id)} />
    ), width: '70px' },
    { name: 'Actions', cell: row => (
      <div style={{ display: 'flex', gap: '6px' }}>
        <button className="btn btn-primary" style={{ fontSize: isMobile ? '13px' : '12px', padding: isMobile ? '8px 12px' : '4px 8px' }} onClick={() => openEditModal(row)} disabled={deletingProductId === row.id}>Edit</button>
        <button className="btn btn-danger" style={{ fontSize: isMobile ? '13px' : '12px', padding: isMobile ? '8px 12px' : '4px 8px' }} onClick={() => handleDeleteProduct(row)} disabled={deletingProductId === row.id}>
          {deletingProductId === row.id ? 'Deleting...' : 'Delete'}
        </button>
      </div>
    ), width: '180px' }
  ];

  return (
    <div className="container">
      <div className="card">
        <div className="order-detail-header">
          <div className="order-detail-title">
            <div className="breadcrumb"><Link to="/orders">Dashboard</Link> / B2B Products</div>
            <h1>B2B Products</h1>
          </div>
          <div className="order-actions">
            <button className="btn btn-success" onClick={() => { resetProductForm(); setProductForm(f => ({ ...f, warehouse_id: selectedWarehouse })); setShowAddProductModal(true); }}>+ Add Product</button>
          </div>
        </div>

        {/* Hub Selector */}
        <div style={{ marginBottom: '20px' }}>
          <select className="form-control" style={{ width: '100%', maxWidth: '250px' }} value={selectedWarehouse} onChange={(e) => setSelectedWarehouse(e.target.value)}>
            <option value="">Select Hub</option>
            {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        </div>

        <div className="card">
          <DataTable columns={columns} data={products} pagination paginationPerPage={20} progressPending={loading} highlightOnHover striped defaultSortFieldId={1} defaultSortAsc={true} noDataComponent={selectedWarehouse ? "No B2B products configured for this hub" : "Select a hub to view products"} />
        </div>
      </div>

      {/* Add/Edit Product Modal */}
      {(showAddProductModal || showEditProductModal) && (
        <div className="modal-overlay" onClick={() => { setShowAddProductModal(false); setShowEditProductModal(false); }}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header"><h3>{showEditProductModal ? 'Edit B2B Product' : 'Add B2B Product'}</h3><button className="modal-close" onClick={() => { setShowAddProductModal(false); setShowEditProductModal(false); }}>×</button></div>
            <div className="modal-body">
              <div className="order-info">
                {!showEditProductModal && (
                  <>
                    <div className="info-item">
                      <label className="info-label">Product *</label>
                      <select className="form-control" value={productForm.product_id} onChange={e => setProductForm({ ...productForm, product_id: e.target.value })}>
                        <option value="">Select Product</option>
                        {catalogProducts.map(p => (
                          <option key={p.id} value={p.id}>{p.skuCode} - {p.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="info-item">
                      <label className="info-label">Warehouses *</label>
                      <div style={{ maxHeight: '120px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px', padding: '8px' }}>
                        {warehouses.map(w => (
                          <label key={w.id} style={{ display: 'block', cursor: 'pointer', padding: '2px 0' }}>
                            <input type="checkbox" checked={selectedModalWarehouses.includes(String(w.id))} onChange={(e) => {
                              if (e.target.checked) setSelectedModalWarehouses(prev => [...prev, String(w.id)]);
                              else setSelectedModalWarehouses(prev => prev.filter(id => id !== String(w.id)));
                            }} style={{ marginRight: '8px' }} />
                            {w.name}
                          </label>
                        ))}
                      </div>
                    </div>
                  </>
                )}
                {showEditProductModal && (
                  <div className="info-item">
                    <label className="info-label">Replicate to Warehouses (optional)</label>
                    <div style={{ maxHeight: '120px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px', padding: '8px' }}>
                      {warehouses.map(w => (
                        <label key={w.id} style={{ display: 'block', cursor: 'pointer', padding: '2px 0' }}>
                          <input type="checkbox" checked={selectedModalWarehouses.includes(String(w.id))} onChange={(e) => {
                            if (e.target.checked) setSelectedModalWarehouses(prev => [...prev, String(w.id)]);
                            else setSelectedModalWarehouses(prev => prev.filter(id => id !== String(w.id)));
                          }} style={{ marginRight: '8px' }} />
                          {w.name}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                <div className="info-item">
                  <label className="info-label">MOQ *</label>
                  <input type="number" className="form-control" value={productForm.min_order_qty} onChange={e => setProductForm({ ...productForm, min_order_qty: e.target.value })} />
                </div>
                <div className="info-item">
                  <label className="info-label">Increment *</label>
                  <input type="number" className="form-control" value={productForm.increment_count} onChange={e => setProductForm({ ...productForm, increment_count: e.target.value })} />
                </div>
                <div className="info-item">
                  <label className="info-label">B2B Price/Unit *</label>
                  <input type="number" className="form-control" value={productForm.b2b_price_per_unit} onChange={e => setProductForm({ ...productForm, b2b_price_per_unit: e.target.value })} />
                </div>
                <div className="info-item">
                  <label className="info-label">Advance %</label>
                  <input type="number" className="form-control" value={productForm.advance_pct} onChange={e => setProductForm({ ...productForm, advance_pct: e.target.value })} />
                </div>
                <div className="info-item">
                  <label className="info-label">Free Delivery</label>
                  <select className="form-control" value={String(productForm.free_delivery_allowed)} onChange={e => setProductForm({ ...productForm, free_delivery_allowed: e.target.value === 'true' })}>
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </select>
                </div>
                <div className="info-item">
                  <label className="info-label">Delivery Fee/MOQ</label>
                  <input type="number" className="form-control" value={productForm.delivery_fee_per_moq} onChange={e => setProductForm({ ...productForm, delivery_fee_per_moq: e.target.value })} />
                </div>
                <div className="info-item">
                  <label className="info-label">Validity Start *</label>
                  <input type="date" className="form-control" value={productForm.price_validity_start} onChange={e => setProductForm({ ...productForm, price_validity_start: e.target.value })} />
                </div>
                <div className="info-item">
                  <label className="info-label">Validity End *</label>
                  <input type="date" className="form-control" value={productForm.price_validity_end} onChange={e => setProductForm({ ...productForm, price_validity_end: e.target.value })} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => { setShowAddProductModal(false); setShowEditProductModal(false); }}>Cancel</button>
              <button className="btn btn-success" onClick={showEditProductModal ? handleUpdateProduct : handleAddProduct}>
                {showEditProductModal ? 'Update' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default B2BProducts;
